import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { buildApiUrl, API_CONFIG } from '../config/api';
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  EyeIcon,
  UserIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ServerIcon,
  KeyIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';

interface DatabaseInstance {
  id: string;
  name: string;
  host: string;
  port: number;
  database: string;
  username: string;
  is_active: boolean;
}

interface DatabaseUser {
  username: string;
  superuser: boolean;
  create_db: boolean;
  create_role: boolean;
  valid_until: string;
  conn_limit: number;
}

interface UserCreateData {
  username: string;
  password?: string;
  databases: string[];
  privileges: string[];
  expire_days: number;
}

interface UserUpdateData {
  password?: string;
  databases?: string[];
  privileges?: string[];
  expire_days?: number;
}

const DatabaseUsers: React.FC = () => {
  const [instances, setInstances] = useState<DatabaseInstance[]>([]);
  const [selectedInstance, setSelectedInstance] = useState<string>('');
  const [users, setUsers] = useState<DatabaseUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<DatabaseUser | null>(null);
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [userPrivileges, setUserPrivileges] = useState<{[key: string]: any}>({});
  const [availableDatabases, setAvailableDatabases] = useState<string[]>([]);

  const [newUser, setNewUser] = useState<UserCreateData>({
    username: '',
    password: '',
    databases: [],
    privileges: ['SELECT', 'INSERT', 'UPDATE', 'DELETE'],
    expire_days: 30
  });

  const [updateUserData, setUpdateUserData] = useState<UserUpdateData>({
    password: '',
    databases: [],
    privileges: ['SELECT', 'INSERT', 'UPDATE', 'DELETE'],
    expire_days: 30
  });

  const availablePrivileges = [
    'SELECT', 'INSERT', 'UPDATE', 'DELETE', 'ALL'
  ];

  useEffect(() => {
    fetchInstances();
  }, []);

  useEffect(() => {
    if (selectedInstance) {
      fetchUsers();
      fetchDatabases();
    }
  }, [selectedInstance]);

  const fetchInstances = async () => {
    try {
      setLoading(true);
      const response = await axios.get(buildApiUrl(API_CONFIG.ENDPOINTS.DATABASE_INSTANCES));
      const allInstances = response.data.data.instances || [];
      // Only show active instances for user management
      const activeInstances = allInstances.filter((instance: DatabaseInstance) => instance.is_active);
      setInstances(activeInstances);
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to fetch instances');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    if (!selectedInstance) return;
    
    try {
      setLoading(true);
      const response = await axios.get(buildApiUrl(API_CONFIG.ENDPOINTS.POSTGRESQL_USERS), {
        params: { instance_id: selectedInstance }
      });
      setUsers(response.data.data?.users || []);
      setError(null); // Clear any previous errors
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchDatabases = async () => {
    if (!selectedInstance) return;
    
    try {
      const response = await axios.get(buildApiUrl(API_CONFIG.ENDPOINTS.POSTGRESQL_DATABASES), {
        params: { instance_id: selectedInstance }
      });
      const databases = response.data.data?.databases || [];
      setAvailableDatabases(databases.map((db: any) => db.name));
    } catch (error: any) {
      console.error('Failed to fetch databases:', error);
      setAvailableDatabases([]);
    }
  };

  const createUser = async () => {
    if (!selectedInstance) return;
    
    try {
      const userData = {
        username: newUser.username,
        password: newUser.password,
        superuser: false,
        create_db: false,
        create_role: false,
        conn_limit: 10,
        valid_until: newUser.expire_days > 0 ? 
          new Date(Date.now() + newUser.expire_days * 24 * 60 * 60 * 1000).toISOString().split('T')[0] : '',
        databases: newUser.databases,
        privileges: newUser.privileges
      };

      const response = await axios.post(buildApiUrl(API_CONFIG.ENDPOINTS.POSTGRESQL_USERS), userData, {
        params: { instance_id: selectedInstance }
      });
      
      setShowCreateModal(false);
      setNewUser({
        username: '',
        password: '',
        databases: [],
        privileges: ['SELECT', 'INSERT', 'UPDATE', 'DELETE'],
        expire_days: 30
      });
      
      alert(`User created successfully!\nUsername: ${newUser.username}\nPassword: ${newUser.password}`);
      fetchUsers();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to create user');
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedInstance || !selectedUser) return;
    
    try {
      const userData = {
        password: updateUserData.password,
        superuser: false,
        create_db: false,
        create_role: false,
        conn_limit: 10,
        valid_until: (updateUserData.expire_days && updateUserData.expire_days > 0) ? 
          new Date(Date.now() + updateUserData.expire_days * 24 * 60 * 60 * 1000).toISOString().split('T')[0] : '',
        databases: updateUserData.databases,
        privileges: updateUserData.privileges
      };

      await axios.put(buildApiUrl(`${API_CONFIG.ENDPOINTS.POSTGRESQL_USERS}/${selectedUser.username}`), userData, {
        params: { instance_id: selectedInstance }
      });
      
      setShowUpdateModal(false);
      setSelectedUser(null);
      setUpdateUserData({
        password: '',
        databases: [],
        privileges: ['SELECT', 'INSERT', 'UPDATE', 'DELETE'],
        expire_days: 30
      });
      
      alert(`User updated successfully!\nUser: ${selectedUser.username}`);
      fetchUsers();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to update user');
    }
  };

  const deleteUser = async (username: string) => {
    if (!selectedInstance || !window.confirm(`Are you sure you want to delete user ${username}?`)) return;
    
    try {
      await axios.delete(buildApiUrl(`${API_CONFIG.ENDPOINTS.POSTGRESQL_USERS}/${username}`), {
        params: { instance_id: selectedInstance }
      });
      
      alert(`User deleted successfully!\nUser: ${username}`);
      fetchUsers();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to delete user');
    }
  };

  const getUserPrivileges = async (username: string) => {
    if (!selectedInstance) return;
    
    try {
      const response = await axios.get(buildApiUrl(`${API_CONFIG.ENDPOINTS.POSTGRESQL_USERS}/${username}/permissions`), {
        params: { instance_id: selectedInstance }
      });
      
      const permissions = response.data.data?.permissions;
      if (permissions) {
        setUserPrivileges(prev => ({
          ...prev,
          [username]: permissions
        }));
        
        // Toggle expanded state
        if (expandedUser === username) {
          setExpandedUser(null);
        } else {
          setExpandedUser(username);
        }
      }
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to fetch user privileges');
    }
  };

  const revokeUserPermissions = async (username: string, databases: string[], privileges: string[] = []) => {
    if (!selectedInstance) return;
    
    try {
      const revokeData = {
        databases: databases,
        privileges: privileges
      };

      await axios.post(buildApiUrl(`${API_CONFIG.ENDPOINTS.POSTGRESQL_USERS}/${username}/revoke-permissions`), revokeData, {
        params: { instance_id: selectedInstance }
      });
      
      alert(`Permissions revoked successfully!\nUser: ${username}\nDatabases: ${databases.join(', ')}\nPrivileges: ${privileges.length > 0 ? privileges.join(', ') : 'All'}`);
      fetchUsers();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to revoke permissions');
    }
  };

  const getStatusIcon = (user: DatabaseUser) => {
    if (user.superuser) {
      return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
    }
    if (user.valid_until && new Date(user.valid_until) < new Date()) {
      return <XCircleIcon className="h-5 w-5 text-red-500" />;
    }
    return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
  };

  const getStatusText = (user: DatabaseUser) => {
    if (user.superuser) return 'Superuser';
    if (user.valid_until && new Date(user.valid_until) < new Date()) return 'Expired';
    return 'Active';
  };

  if (loading && !selectedInstance) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Database Users</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage users and permissions for database instances
          </p>
        </div>
        {selectedInstance && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Create User
          </button>
        )}
      </div>

      {error && (
        <div className="alert-danger">
          <p>{error}</p>
        </div>
      )}

      {/* Instance Selector */}
      <div className="bg-white p-4 rounded-lg shadow border">
        <h3 className="text-lg font-medium text-gray-900 mb-3">Select Database Instance</h3>
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <select
              value={selectedInstance}
              onChange={(e) => setSelectedInstance(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Select a database instance</option>
              {instances.map((instance) => (
                <option key={instance.id} value={instance.id}>
                  {instance.name} ({instance.host}:{instance.port}) - {instance.username}
                </option>
              ))}
            </select>
          </div>
          {selectedInstance && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">Status:</span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Connected
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Users Table */}
      {selectedInstance && (
        <div className="bg-white rounded-lg shadow border">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Users</h3>
            {/* <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-md">
              <p className="text-sm text-green-800">
                <strong>✅ Fully Implemented:</strong> Database user management is now complete! 
                You can create, update, delete users and manage their permissions on active database instances.
              </p>
            </div> */}
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="loading-spinner"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Permissions
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Expires
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <React.Fragment key={user.username}>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <UserIcon className="h-5 w-5 text-gray-400 mr-2" />
                            <div>
                              <div className="text-sm font-medium text-gray-900">{user.username}</div>
                              <div className="text-sm text-gray-500">
                                {user.superuser ? 'Superuser' : 'Regular User'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {getStatusIcon(user)}
                            <span className="ml-2 text-sm text-gray-900">{getStatusText(user)}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {user.create_db && <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded mr-1">Create DB</span>}
                            {user.create_role && <span className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded mr-1">Create Role</span>}
                            <span className="inline-block bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded">Limit: {user.conn_limit}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <CalendarIcon className="h-4 w-4 text-gray-400 mr-1" />
                            <span className="text-sm text-gray-900">
                              {user.valid_until ? new Date(user.valid_until).toLocaleDateString() : 'Never'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => getUserPrivileges(user.username)}
                              className={`${expandedUser === user.username ? 'text-primary-800 bg-primary-100' : 'text-primary-600 hover:text-primary-900'} px-2 py-1 rounded`}
                              title="View Privileges"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedUser(user);
                                setUpdateUserData({
                                  password: '',
                                  databases: [],
                                  privileges: ['SELECT', 'INSERT', 'UPDATE', 'DELETE'],
                                  expire_days: 30
                                });
                                setShowUpdateModal(true);
                              }}
                              className="text-blue-600 hover:text-blue-900"
                              title="Edit User"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => {
                                const databases = availableDatabases;
                                const privileges = ['SELECT', 'INSERT', 'UPDATE', 'DELETE'];
                                if (window.confirm(`Revoke all permissions for user ${user.username}?`)) {
                                  revokeUserPermissions(user.username, databases, []);
                                }
                              }}
                              className="text-orange-600 hover:text-orange-900"
                              title="Revoke All Permissions"
                            >
                              <KeyIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => deleteUser(user.username)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete User"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      
                      {/* Expanded permissions row */}
                      {expandedUser === user.username && userPrivileges[user.username] && (
                        <tr className="bg-gray-50">
                          <td colSpan={5} className="px-6 py-4">
                            <div className="space-y-4">
                              {/* User Information */}
                              <div>
                                <h4 className="text-sm font-medium text-gray-900 mb-2">User Information</h4>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <span className="font-medium text-gray-600">Username:</span> {user.username}
                                  </div>
                                  <div>
                                    <span className="font-medium text-gray-600">Superuser:</span> {user.superuser ? 'Yes' : 'No'}
                                  </div>
                                  <div>
                                    <span className="font-medium text-gray-600">Can Create DB:</span> {user.create_db ? 'Yes' : 'No'}
                                  </div>
                                  <div>
                                    <span className="font-medium text-gray-600">Expires:</span> {user.valid_until ? new Date(user.valid_until).toLocaleDateString() : 'Never'}
                                  </div>
                                  {userPrivileges[user.username]?.user_info?.nologin !== undefined && (
                                    <div>
                                      <span className="font-medium text-gray-600">Login Status:</span> 
                                      <span className={`ml-1 px-2 py-1 rounded text-xs ${
                                        userPrivileges[user.username].user_info.nologin 
                                          ? 'bg-red-100 text-red-800' 
                                          : 'bg-green-100 text-green-800'
                                      }`}>
                                        {userPrivileges[user.username].user_info.nologin ? 'NOLOGIN (Disabled)' : 'LOGIN (Enabled)'}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              {/* Database Privileges */}
                              <div>
                                <h4 className="text-sm font-medium text-gray-900 mb-2">Database Privileges</h4>
                                <div className="bg-white p-3 rounded border">
                                  {userPrivileges[user.username].databases && Object.keys(userPrivileges[user.username].databases).length > 0 ? (
                                    <div className="space-y-2">
                                      {Object.entries(userPrivileges[user.username].databases).map(([db, privs]: [string, any]) => (
                                        <div key={db} className="flex items-center justify-between">
                                          <span className="font-medium text-gray-700">{db}</span>
                                          <div className="flex flex-wrap gap-1">
                                            {privs.map((priv: string) => (
                                              <span key={priv} className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                                                {priv}
                                              </span>
                                            ))}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <p className="text-gray-500 text-sm">No specific database privileges</p>
                                  )}
                                </div>
                              </div>
                              
                              {/* Table Privileges */}
                              {userPrivileges[user.username].tables && Object.keys(userPrivileges[user.username].tables).length > 0 && (
                                <div>
                                  <h4 className="text-sm font-medium text-gray-900 mb-2">Table Privileges</h4>
                                  <div className="bg-white p-3 rounded border max-h-40 overflow-y-auto">
                                    <div className="space-y-2">
                                      {Object.entries(userPrivileges[user.username].tables).map(([db, tables]: [string, any]) => (
                                        <div key={db}>
                                          <div className="font-medium text-gray-700 mb-1">{db}</div>
                                          <div className="ml-4 space-y-1">
                                            {Object.entries(tables).map(([table, privs]: [string, any]) => (
                                              <div key={table} className="flex items-center justify-between text-sm">
                                                <span className="text-gray-600">{table}</span>
                                                <div className="flex flex-wrap gap-1">
                                                  {privs.map((priv: string) => (
                                                    <span key={priv} className={`inline-block text-xs px-2 py-1 rounded ${
                                                      priv === 'SELECT' ? 'bg-blue-100 text-blue-800' :
                                                      priv === 'INSERT' ? 'bg-green-100 text-green-800' :
                                                      priv === 'UPDATE' ? 'bg-yellow-100 text-yellow-800' :
                                                      priv === 'DELETE' ? 'bg-red-100 text-red-800' :
                                                      'bg-gray-100 text-gray-800'
                                                    }`}>
                                                      {priv}
                                                    </span>
                                                  ))}
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}


      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Create Database User</h2>
            <p className="text-sm text-gray-600 mb-4">
              Instance: {instances.find(i => i.id === selectedInstance)?.name}
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Username
                </label>
                <input
                  type="text"
                  value={newUser.username}
                  onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="newuser"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password (leave empty for auto-generate)
                </label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Auto-generate"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Databases
                </label>
                <select
                  multiple
                  value={newUser.databases}
                  onChange={(e) => {
                    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                    setNewUser({...newUser, databases: selectedOptions});
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md min-h-[100px]"
                >
                  {availableDatabases.map((database) => (
                    <option key={database} value={database}>
                      {database}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Hold Ctrl (or Cmd on Mac) to select multiple databases
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Privileges
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {availablePrivileges.map((privilege) => (
                    <label key={privilege} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={newUser.privileges.includes(privilege)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setNewUser({...newUser, privileges: [...newUser.privileges, privilege]});
                          } else {
                            setNewUser({...newUser, privileges: newUser.privileges.filter(p => p !== privilege)});
                          }
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">{privilege}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expire Days
                </label>
                <input
                  type="number"
                  value={newUser.expire_days}
                  onChange={(e) => setNewUser({...newUser, expire_days: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  min="1"
                  max="365"
                />
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={createUser}
                className="btn-primary flex-1"
              >
                Create User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update User Modal */}
      {showUpdateModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Update Database User</h2>
            <p className="text-sm text-gray-600 mb-4">
              User: {selectedUser.username}
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password (leave empty to keep current)
                </label>
                                 <input
                   type="password"
                   value={updateUserData.password}
                   onChange={(e) => setUpdateUserData({...updateUserData, password: e.target.value})}
                   className="w-full px-3 py-2 border border-gray-300 rounded-md"
                   placeholder="Keep current password"
                 />
              </div>
              
                             <div>
                 <label className="block text-sm font-medium text-gray-700 mb-1">
                   Databases
                 </label>
                 <select
                   multiple
                   value={updateUserData.databases || []}
                   onChange={(e) => {
                     const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                     setUpdateUserData({...updateUserData, databases: selectedOptions});
                   }}
                   className="w-full px-3 py-2 border border-gray-300 rounded-md min-h-[100px]"
                 >
                   {availableDatabases.map((database) => (
                     <option key={database} value={database}>
                       {database}
                     </option>
                   ))}
                 </select>
                 <p className="text-xs text-gray-500 mt-1">
                   Hold Ctrl (or Cmd on Mac) to select multiple databases
                 </p>
               </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Privileges
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {availablePrivileges.map((privilege) => (
                                         <label key={privilege} className="flex items-center">
                       <input
                         type="checkbox"
                         checked={updateUserData.privileges?.includes(privilege) || false}
                         onChange={(e) => {
                           const currentPrivileges = updateUserData.privileges || [];
                           if (e.target.checked) {
                             setUpdateUserData({...updateUserData, privileges: [...currentPrivileges, privilege]});
                           } else {
                             setUpdateUserData({...updateUserData, privileges: currentPrivileges.filter(p => p !== privilege)});
                           }
                         }}
                         className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                       />
                       <span className="ml-2 text-sm text-gray-700">{privilege}</span>
                     </label>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Extend Expire Days
                </label>
                                 <input
                   type="number"
                   value={updateUserData.expire_days}
                   onChange={(e) => setUpdateUserData({...updateUserData, expire_days: parseInt(e.target.value)})}
                   className="w-full px-3 py-2 border border-gray-300 rounded-md"
                   min="1"
                   max="365"
                 />
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowUpdateModal(false)}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
                             <button
                 onClick={handleUpdateUser}
                 className="btn-primary flex-1"
               >
                 Update User
               </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatabaseUsers;
